import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Checkbox } from 'primereact/checkbox'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { DateFilterState } from 'shared/date-filter/date-filter-component'
import { vehicleColors, vehicleTypes } from '../types/consts'

const datePresets = [
  { label: 'Hoje', value: 'today' },
  { label: 'Ontem', value: 'yesterday' },
  { label: 'Últimos 7 dias', value: 'last7days' },
  { label: 'Últimos 30 dias', value: 'last30days' },
  { label: 'Personalizado', value: 'custom' },
]

interface ANPREventsFiltersProps {
  dateFilter: DateFilterState
  datePreset: string
  searchButtonHighlight: boolean
  isLoading: boolean
  clientId: string | null
  filteredEventsCount: number
  globalFilterValue: string
  colorFilter: string
  vehicleTypeFilter: string
  speedFilter: number | null
  cameraNameFilter: string
  uniqueCameraNames: { label: string; value: string }[]
  layout: 'grid' | 'list'
  onApplyDatePreset: (preset: string) => void
  onSearch: () => void
  onDateFilterChange: (prop: 'startDate' | 'endDate', value: any) => void
  onGlobalFilterChange: (value: string) => void
  onColorFilterChange: (value: string) => void
  onVehicleTypeFilterChange: (value: string) => void
  onCameraNameFilterChange: (value: string) => void
  onLayoutChange: (layout: 'grid' | 'list') => void
  onSpeedFilterChange: (value: number | null) => void
  showOnlyViolations: boolean
  onShowOnlyViolationsChange: (value: boolean) => void
  onExport: () => void
}

const ANPREventsFilters = ({
  dateFilter,
  datePreset,
  searchButtonHighlight,
  isLoading,
  clientId,
  filteredEventsCount,
  globalFilterValue,
  colorFilter,
  vehicleTypeFilter,
  cameraNameFilter,
  uniqueCameraNames,
  speedFilter,
  layout,
  onApplyDatePreset,
  onSearch,
  onDateFilterChange,
  onGlobalFilterChange,
  onColorFilterChange,
  onVehicleTypeFilterChange,
  onCameraNameFilterChange,
  onLayoutChange,
  onSpeedFilterChange,
  showOnlyViolations,
  onShowOnlyViolationsChange,
  onExport
}: ANPREventsFiltersProps) => {
  return (
    <div className='events-toolbar-left'>
      <div className='date-filter-section'>
        <div className='section-label'>
          <i className='pi pi-calendar' />
          <span>Período de Busca</span>
          <small>(requer nova busca)</small>
        </div>

        <div className='grid'>
          <div className='col-12 xl:col-8'>
            <div className='date-presets'>
              {datePresets.map((preset) => (
                <Button
                  key={preset.value}
                  label={preset.label}
                  className={`preset-button ${datePreset === preset.value ? 'active' : ''}`}
                  onClick={() => onApplyDatePreset(preset.value)}
                  outlined={datePreset !== preset.value}
                />
              ))}
            </div>
          </div>

          {datePreset === 'custom' && (
            <div className='col-12 xl:col-12'>
              <div className='custom-date-inputs'>
                <div className='date-input-group'>
                  <label>De:</label>
                  <Calendar
                    value={dateFilter.startDate}
                    onChange={(e: any) => onDateFilterChange('startDate', e.value)}
                    dateFormat='dd/mm/yy'
                    placeholder='Data inicial'
                    maxDate={dateFilter.endDate}
                    showIcon
                    showTime
                    stepMinute={15}
                    hourFormat='24'
                  />
                </div>
                <div className='date-input-group'>
                  <label>Até:</label>
                  <Calendar
                    value={dateFilter.endDate}
                    onChange={(e: any) => onDateFilterChange('endDate', e.value)}
                    dateFormat='dd/mm/yy'
                    placeholder='Data final'
                    maxDate={new Date()}
                    showIcon
                    showTime
                    stepMinute={15}
                    hourFormat='24'
                  />
                </div>
              </div>
            </div>
          )}

          <div className='col-12 md:col-6 xl:col-2'>
            <Button
              icon='pi pi-search'
              label='Buscar Eventos'
              className={`search-button w-full ${searchButtonHighlight ? 'highlight-pulse' : ''}`}
              onClick={onSearch}
              loading={isLoading}
            />
          </div>

          {clientId && (
            <div className='col-12 md:col-6 xl:col-2'>
              <div className='events-stats'>
                <span className='events-badge'>{filteredEventsCount}</span>
                <span className='events-stats-text'>Total de Eventos</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='client-filters-section'>
        <div className='section-label'>
          <i className='pi pi-filter' />
          <span>Filtros Rápidos</span>
          <small>(aplicados instantaneamente)</small>
        </div>

        <div className='grid'>
          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'>
                <i className='pi pi-search'></i>
              </span>
              <InputText
                value={globalFilterValue}
                onChange={(e) => onGlobalFilterChange(e.target.value)}
                placeholder='Buscar por placa...'
                className='filter-input'
              />
            </div>
          </div>

          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'>
                <i className='pi pi-video'></i>
              </span>
              <Dropdown
                value={cameraNameFilter}
                onChange={(e) => onCameraNameFilterChange(e.value)}
                options={uniqueCameraNames}
                placeholder='Filtrar por câmera'
                className='filter-dropdown'
                showClear={cameraNameFilter !== ''}
              />
            </div>
          </div>

          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'>
                <i className='pi pi-car'></i>
              </span>
              <Dropdown
                value={vehicleTypeFilter}
                onChange={(e) => onVehicleTypeFilterChange(e.value)}
                options={vehicleTypes}
                placeholder='Tipo de veículo'
                className='filter-dropdown'
                showClear={vehicleTypeFilter !== ''}
              />
            </div>
          </div>

          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'>
                <i className='pi pi-palette'></i>
              </span>
              <Dropdown
                value={colorFilter}
                onChange={(e) => onColorFilterChange(e.value)}
                options={vehicleColors}
                placeholder='Cor do veículo'
                className='filter-dropdown'
                showClear={colorFilter !== ''}
              />
            </div>
          </div>
          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'>
                <i className='pi pi-gauge'></i>
              </span>
              <InputNumber
                value={speedFilter}
                onValueChange={(e) => onSpeedFilterChange(e.value ?? null)}
                placeholder='Velocidade mínima (km/h)'
                inputClassName='filter-input'
                min={0}
                step={10}
                max={120}
                showButtons={false}
                suffix=' km/h'
              />
            </div>
          </div>

          <div className='col-12'>
            <div className='filter-group align-items-center gap-2'>
              <Checkbox
                inputId='speed-violations-filter'
                checked={showOnlyViolations}
                onChange={(e) => onShowOnlyViolationsChange(e.checked ?? false)}
              />
              <label htmlFor='speed-violations-filter' className='cursor-pointer'>
                <i className='pi pi-exclamation-triangle mr-1' style={{ color: 'var(--red-500)' }} />
                Apenas infrações de velocidade
              </label>
            </div>
          </div>
        </div>

        <div className='layout-toggle-section'>
          <span className='layout-label'>Visualização:</span>
          <div className='layout-toggle-buttons'>
            <Button
              icon='pi pi-th-large'
              rounded
              text
              severity={layout === 'grid' ? 'info' : 'secondary'}
              onClick={() => onLayoutChange('grid')}
              tooltip='Grade'
              tooltipOptions={{ position: 'top' }}
            />
            <Button
              icon='pi pi-list'
              rounded
              text
              severity={layout === 'list' ? 'info' : 'secondary'}
              onClick={() => onLayoutChange('list')}
              tooltip='Lista'
              tooltipOptions={{ position: 'top' }}
            />

          </div>
          <Button
            icon='pi pi-file-export'
            label={`Exportar (${filteredEventsCount})`}
            className='p-button-outlined p-button-sm'
            onClick={onExport}
            disabled={filteredEventsCount === 0}
          />
        </div>
      </div>
    </div>
  )
}

export default ANPREventsFilters